import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Card, CardBody, CardHeader, Row, Col, Button } from "reactstrap";
import apis from "../../../../Api";
import StudentProgressChartsModal from "./StudentProgressChartsModal.jsx";

function StudentProgressModal({
  isOpen,
  onClose,
  selectedStudent,
  companyId,
  onExport
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [showCharts, setShowCharts] = useState(false);
  const [charts, setCharts] = useState(null);
  const formatDuration = seconds => {
    const s = Number(seconds) || 0;
    if (s <= 0) return "0m";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    const parts = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    if (h === 0 && m === 0 && r > 0) parts.push(`${r}s`);
    return parts.join(" ") || "0m";
  };

  const formatDateTime = value => {
    if (!value) return null;
    const moment = require("moment");
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : null;
  };
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!isOpen) return;
      if (!selectedStudent || !selectedStudent.id) {
        setData(null);
        setError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      setData(null);
      try {
        const resp = await apis.getUserProgressDetail(selectedStudent.id, companyId).catch(() => null);
        if (!mounted) return;
        setData(resp?.data || resp || null);
      } catch (err) {
        console.error('Erro ao carregar progresso detalhado do usuário (modal):', err);
        if (!mounted) return;
        const msg = err?.response?.data?.msg || err?.message || "Não foi possível carregar o progresso detalhado deste aluno.";
        setError(msg);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [isOpen, selectedStudent, companyId]);

  // Do not return early before hooks run - keep hooks invocation order stable.
  if (!isOpen) return null;

  const payload = data || {};
  const userData = { ...(selectedStudent || {}), ...(payload.user || {}) };
  const classrooms = Array.isArray(payload.classrooms) ? payload.classrooms : [];

  const aggregate = (classrooms || []).reduce((acc, classroom) => {
    const summary = classroom?.summary || {};
    acc.totalSeconds += Number(summary.totalSeconds) || 0;
    acc.totalLessons += Number(summary.lessons) || 0;
    if (Array.isArray(classroom?.units)) acc.totalUnits += classroom.units.length;
    const last = summary.lastActivity;
    if (last) {
      const moment = require("moment");
      const lastMoment = moment(last);
      if (lastMoment.isValid() && (!acc.lastActivity || lastMoment.isAfter(acc.lastActivity))) acc.lastActivity = lastMoment;
    }
    return acc;
  }, { totalSeconds: 0, totalLessons: 0, totalUnits: 0, lastActivity: null });

  const lastActivityFormatted = aggregate.lastActivity ? aggregate.lastActivity.format("DD/MM/YYYY HH:mm") : null;
  const studentSummary = selectedStudent?.progressSummary || {};
  const headerHighlights = [
    { label: "Turmas", value: classrooms.length },
    { label: "Unidades", value: aggregate.totalUnits },
    { label: "Aulas concluídas", value: aggregate.totalLessons },
    { label: "Tempo estudado", value: formatDuration(aggregate.totalSeconds) }
  ];
  const detailHighlights = [
    { label: "Sessões", value: studentSummary.sessionsCount || 0 },
    { label: "Aulas distintas", value: studentSummary.lessonsCount || 0 },
    { label: "Tempo consolidado", value: formatDuration(studentSummary.totalSeconds || 0) }
  ];
  const lastActivityDisplay = studentSummary.lastActivity ? formatDateTime(studentSummary.lastActivity) : (lastActivityFormatted || "Sem registro");

  // helpers moved from parent
  const resolveLessonTypeLabel = (type) => {
    const raw = type === null || type === undefined ? null : String(type).trim();
    const numeric = raw !== null ? Number(raw) : Number.NaN;
    const typeMap = {
      0: "Texto",
      1: "Texto",
      2: "Conteúdo externo",
      3: "Quiz",
      4: "Arquivo",
      5: "Vídeo"
    };

    if (!Number.isNaN(numeric) && typeMap[numeric]) {
      return typeMap[numeric];
    }

    if (raw) {
      const semanticMap = {
        texto: "Texto",
        html: "Texto",
        video: "Vídeo",
        quiz: "Quiz",
        iframe: "Conteúdo externo",
        embed: "Conteúdo externo"
      };
      const candidate = semanticMap[raw.toLowerCase()];
      return candidate || `Outro (${raw})`;
    }

    return "Outro";
  };

  const buildStudentProgressCharts = (classrooms = []) => {
    const classroomTime = [];
    const classroomLessons = [];
    const unitAggregates = new Map();
    const lessonTypeAggregates = new Map();

    classrooms.forEach(classroom => {
      if (!classroom) return;
      const summary = classroom.summary || {};
      const totalSeconds = Number(summary.totalSeconds) || 0;
      const lessonsCount = Number(summary.lessons) || 0;
      const classroomName = classroom.name || classroom.course?.name || `Turma ${classroom.id || "-"}`;

      classroomTime.push({ name: classroomName, totalSeconds, totalMinutes: Math.round(totalSeconds / 60) });
      classroomLessons.push({ name: classroomName, lessons: lessonsCount });

      const units = Array.isArray(classroom.units) ? classroom.units : [];
      units.forEach(unit => {
        if (!unit) return;
        const unitSummary = unit.summary || {};
        const unitSeconds = Number(unitSummary.totalSeconds) || 0;
        const unitLessons = Number(unitSummary.lessons) || 0;
        const unitName = unit.name || "Unidade";

        const aggregateUnit = unitAggregates.get(unitName) || { name: unitName, totalSeconds: 0, lessons: 0 };
        aggregateUnit.totalSeconds += unitSeconds;
        aggregateUnit.lessons += unitLessons;
        unitAggregates.set(unitName, aggregateUnit);

        const lessons = Array.isArray(unit.lessons) ? unit.lessons : [];
        lessons.forEach(lesson => {
          if (!lesson) return;
          const typeLabel = resolveLessonTypeLabel(lesson.type);
          const aggregateLesson = lessonTypeAggregates.get(typeLabel) || { label: typeLabel, count: 0, totalSeconds: 0 };
          aggregateLesson.count += 1;
          aggregateLesson.totalSeconds += Number(lesson.totalSeconds) || 0;
          lessonTypeAggregates.set(typeLabel, aggregateLesson);
        });
      });
    });

    const limit = 15;
    const classroomTimeData = [...classroomTime].sort((a, b) => b.totalSeconds - a.totalSeconds).slice(0, limit);
    const classroomLessonsData = [...classroomLessons].sort((a, b) => b.lessons - a.lessons).slice(0, limit);
    const unitTimeData = Array.from(unitAggregates.values()).sort((a, b) => b.totalSeconds - a.totalSeconds).slice(0, limit);
    const lessonTypeData = Array.from(lessonTypeAggregates.values()).sort((a, b) => b.count - a.count);

    return {
      classroomTime: classroomTimeData,
      classroomLessons: classroomLessonsData,
      unitTime: unitTimeData,
      lessonTypes: lessonTypeData
    };
  };

  const openCharts = () => {
    const classroomsList = Array.isArray(payload.classrooms) ? payload.classrooms : [];
    if (!classroomsList.length) return;
    const built = buildStudentProgressCharts(classroomsList);
    setCharts(built);
    setShowCharts(true);
  };

  const handleExport = () => {
    const classroomsList = Array.isArray(payload.classrooms) ? payload.classrooms : [];
    if (!classroomsList.length) return;

    const rows = [];
    const studentId = userData?.id || "";
    const studentName = userData?.name || "";
    const studentLastName = userData?.lastName || "";
    const studentEmail = userData?.email || "";

    classroomsList.forEach(classroom => {
      const classroomId = classroom?.id || "";
      const classroomName = classroom?.name || "";
      const courseId = classroom?.course?.id || "";
      const courseName = classroom?.course?.name || "";
      const units = Array.isArray(classroom.units) ? classroom.units : [];
      units.forEach(unit => {
        const unitId = unit?.id || "";
        const unitName = unit?.name || "";
        const lessons = Array.isArray(unit.lessons) ? unit.lessons : [];
        lessons.forEach(lesson => {
          const lessonId = lesson?.id || "";
          const lessonName = lesson?.name || "";
          const lessonType = resolveLessonTypeLabel(lesson?.type);
          const attempts = lesson?.attempts || 0;
          const timeSeconds = Number(lesson?.totalSeconds) || 0;
          const timeFormatted = formatDuration(timeSeconds);
          const lastActivity = lesson?.lastActivity || "";
          const lastActivityFormatted = lastActivity ? formatDateTime(lastActivity) : "";
          const quizScore = lesson?.quizScore !== undefined ? lesson.quizScore : "";

          rows.push({
            studentId,
            studentName,
            studentLastName,
            studentEmail,
            classroomId,
            classroomName,
            courseId,
            courseName,
            unitId,
            unitName,
            lessonId,
            lessonName,
            lessonType,
            attempts,
            timeSeconds,
            timeFormatted,
            lastActivity,
            lastActivityFormatted,
            quizScore
          });
        });
      });
    });

    if (rows.length > 0) {
      if (typeof onExport === 'function') onExport(rows);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" className="modal-dialog-scrollable" style={{ maxWidth: "90%", maxHeight: "85vh" }}>
      <ModalHeader toggle={onClose}>Progresso detalhado de {userData.name} {userData.lastName}</ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"><span className="sr-only">Loading...</span></div>
          </div>
        ) : null}

        {!loading && error ? (
          <div className="alert alert-danger" role="alert">{error}</div>
        ) : null}

        {!loading && !error && (
          <>
            <Card className="mb-4 border-0 shadow-sm">
              <CardBody className="p-4">
                <Row className="align-items-center">
                  <Col md="4" className="d-flex align-items-center mb-3 mb-md-0">
                    <div className="mr-3" style={{ width: "72px", height: "72px", borderRadius: "50%", overflow: "hidden" }}>
                      <img src={userData.photo || "/images/defaultAvatar.png"} alt={userData.name || "Aluno"} className="rounded-circle" style={{ width: "72px", height: "72px", objectFit: "cover" }} />
                    </div>
                    <div>
                      <div className="h4 mb-1">{userData.name} {userData.lastName}</div>
                      <div className="text-sm text-muted">{userData.email || "Sem e-mail"}</div>
                      <div className="text-sm text-muted">{userData.roleLabel ? `${userData.roleLabel} · ` : ""}{userData.statusLabel || "Status desconhecido"}</div>
                    </div>
                  </Col>
                  <Col md="8">
                    <Row>
                      {headerHighlights.map(highlight => (
                        <Col key={highlight.label} xs="6" md="3" className="mb-3 text-center">
                          <div className="text-uppercase text-muted small">{highlight.label}</div>
                          <div className="h5 mb-0">{highlight.value}</div>
                        </Col>
                      ))}
                      <Col xs="12" className="text-right mt-1">
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', alignItems: 'center' }}>
                          <span className="text-sm text-muted">Última atividade: {lastActivityDisplay}</span>
                          {Array.isArray(classrooms) && classrooms.length > 0 ? (
                            <Button size="sm" color="primary" outline onClick={openCharts}>Ver gráficos</Button>
                          ) : null}
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="mb-4 border-0 shadow-sm">
              <CardBody className="p-3">
                <Row className="text-center">
                  {detailHighlights.map(highlight => (
                    <Col key={highlight.label} xs="12" md="4" className="mb-3">
                      <div className="text-uppercase text-muted small">{highlight.label}</div>
                      <div className="h5 mb-0">{highlight.value}</div>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>

            {classrooms.length === 0 ? (
              <div className="text-center text-muted py-5">Nenhum registro detalhado foi encontrado para este aluno.</div>
            ) : (
              classrooms.map(classroom => {
                const classroomSummary = classroom.summary || {};
                const classroomLastActivity = classroomSummary.lastActivity ? formatDateTime(classroomSummary.lastActivity) : null;
                const units = Array.isArray(classroom.units) ? classroom.units : [];
                return (
                  <Card key={classroom.id || classroom.name} className="mb-4 shadow-sm">
                    <CardHeader className="bg-transparent border-0 pb-0">
                      <Row className="align-items-center">
                        <Col md="7">
                          <div className="h5 mb-1">{classroom.name || "Turma"}</div>
                          <div className="text-sm text-muted">{classroom.course?.name || "Curso não informado"}</div>
                        </Col>
                        <Col md="5" className="mt-3 mt-md-0">
                          <Row className="text-center">
                            <Col xs="4" className="mb-3">
                              <div className="text-uppercase text-muted small">Aulas</div>
                              <div className="h6 mb-0">{classroomSummary.lessons || 0}</div>
                            </Col>
                            <Col xs="4" className="mb-3">
                              <div className="text-uppercase text-muted small">Tempo</div>
                              <div className="h6 mb-0">{formatDuration(classroomSummary.totalSeconds || 0)}</div>
                            </Col>
                            <Col xs="4" className="mb-3">
                              <div className="text-uppercase text-muted small">Última atividade</div>
                              <div className="h6 mb-0" style={{ minWidth: "80px" }}>{classroomLastActivity || "Sem registro"}</div>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </CardHeader>
                    <CardBody className="pt-3">
                      {units.length === 0 ? (
                        <div className="text-muted">Nenhuma unidade com progresso encontrada nesta turma.</div>
                      ) : (
                        units.map((unit, index) => {
                          const unitSummary = unit.summary || {};
                          const unitLastActivity = unitSummary.lastActivity ? formatDateTime(unitSummary.lastActivity) : null;
                          const lessons = Array.isArray(unit.lessons) ? unit.lessons : [];
                          return (
                            <div key={unit.id || unit.name || index} className={`pb-3 ${index !== 0 ? "pt-3 border-top" : ""}`}>
                              <Row className="align-items-center mb-2">
                                <Col md="7">
                                  <div className="font-weight-bold text-primary mb-1">{unit.name || "Unidade"}</div>
                                  <div className="text-sm text-muted">{unitSummary.lessons || 0} aulas · {formatDuration(unitSummary.totalSeconds || 0)}</div>
                                </Col>
                                <Col md="5" className="text-md-right mt-2 mt-md-0">
                                  <span className="text-sm text-muted">Última atividade: {unitLastActivity || "-"}</span>
                                </Col>
                              </Row>
                              {lessons.length === 0 ? (
                                <div className="text-muted mb-3">Nenhuma aula concluída nesta unidade.</div>
                              ) : (
                                <div className="table-responsive">
                                  <table className="table table-sm table-striped mb-0">
                                    <thead>
                                      <tr>
                                        <th style={{ minWidth: "220px" }}>Aula</th>
                                        <th style={{ minWidth: "120px" }}>Tipo</th>
                                        <th style={{ minWidth: "110px" }}>Tentativas</th>
                                        <th style={{ minWidth: "110px" }}>Tempo</th>
                                        <th style={{ minWidth: "160px" }}>Última atividade</th>
                                        <th style={{ minWidth: "110px" }}>Quiz</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {lessons.map(lesson => (
                                        <tr key={lesson.id || lesson.name}>
                                          <td>{lesson.name || "Aula"}</td>
                                          <td>{(() => {
                                            const raw = lesson.type === null || lesson.type === undefined ? null : String(lesson.type).trim();
                                            const numeric = raw !== null ? Number(raw) : Number.NaN;
                                            const typeMap = { 0: "Texto", 1: "Texto", 2: "Conteúdo externo", 3: "Quiz", 4: "Arquivo", 5: "Vídeo" };
                                            if (!Number.isNaN(numeric) && typeMap[numeric]) return typeMap[numeric];
                                            if (raw) {
                                              const semanticMap = { texto: "Texto", html: "Texto", video: "Vídeo", quiz: "Quiz", iframe: "Conteúdo externo", embed: "Conteúdo externo" };
                                              const candidate = semanticMap[raw.toLowerCase()];
                                              return candidate || `Outro (${raw})`;
                                            }
                                            return "Outro";
                                          })()}</td>
                                          <td>{lesson.attempts || 0}</td>
                                          <td>{formatDuration(lesson.totalSeconds || 0)}</td>
                                          <td>{lesson.lastActivity ? formatDateTime(lesson.lastActivity) : "-"}</td>
                                          <td>{lesson.quizScore !== null && lesson.quizScore !== undefined ? `${lesson.quizScore}%` : "-"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </CardBody>
                  </Card>
                );
              })
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
          <Button color="primary" className="mr-2" onClick={handleExport} disabled={!Array.isArray(payload.classrooms) || payload.classrooms.length === 0}>Exportar CSV</Button>
          {userData?.id ? (
            <Button color="link" onClick={onClose}>Fechar</Button>
          ) : (
            <Button color="secondary" onClick={onClose}>Fechar</Button>
          )}
        </div>
      </ModalFooter>
      <StudentProgressChartsModal isOpen={showCharts} onClose={() => setShowCharts(false)} charts={charts || {}} studentName={`${userData.name || ''} ${userData.lastName || ''}`.trim() || 'Aluno'} />
    </Modal>
  );
}

export default StudentProgressModal;
